import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendUp, TrendDown, Users, CreditCard, Gift, Star, Calendar, ArrowRight, DownloadSimple } from '@phosphor-icons/react';
import { useLanguage } from '../contexts/LanguageContext';
import { exportFullAnalytics } from '../services/CSVExportService';
import { toast } from 'sonner';

// habitué color palette
const COLORS = {
  gold: '#C3A365',
  green: '#3E5248',
  darkGreen: '#1E2F26',
  lightGreen: '#8BA89A',
  white: '#E8EBE9',
  accent1: '#627EEA',
  accent2: '#F7931A',
  accent3: '#9945FF',
};

const PIE_COLORS = [COLORS.gold, COLORS.accent1, COLORS.accent2, COLORS.accent3, COLORS.lightGreen];

// Custom tooltip component - defined outside to prevent re-creation
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1E2F26] border border-[#3E5248] rounded-lg p-3 shadow-lg">
        <p className="text-[#8BA89A] text-xs mb-1">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-white text-sm font-medium">
            {entry.name}: {entry.name.includes('£') || entry.dataKey.includes('revenue') 
              ? `£${entry.value.toFixed(2)}` 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Metric Card component - defined outside to prevent re-creation
const MetricCard = ({ icon: Icon, title, value, change, prefix = '', suffix = '' }) => {
  const isPositive = change >= 0;
  return (
    <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-[#C3A365]" />
        <span className="text-[#8BA89A] text-xs uppercase tracking-wider">{title}</span>
      </div>
      <div className="flex items-end justify-between">
        <span className="text-2xl text-white font-light">
          {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
        </span>
        {change !== undefined && (
          <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
            {isPositive ? <TrendUp size={14} /> : <TrendDown size={14} />}
            <span>{isPositive ? '+' : ''}{change}%</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Legend formatter - defined outside
const legendFormatter = (value) => <span className="text-[#8BA89A] text-xs">{value}</span>;

const MerchantAnalytics = ({ merchantId }) => {
  const { language } = useLanguage();
  const [period, setPeriod] = useState('week'); // week, month, year
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);

  const t = {
    fr: {
      title: 'Analyses & Statistiques',
      period: 'Période',
      week: '7 jours',
      month: '30 jours',
      year: '12 mois',
      totalRevenue: 'Chiffre d\'affaires',
      transactions: 'Transactions',
      avgTicket: 'Panier moyen',
      newCustomers: 'Nouveaux clients',
      repeatRate: 'Taux de fidélité',
      pointsIssued: 'Points distribués',
      revenueOverTime: 'Évolution du CA',
      customerLoyalty: 'Fidélité clients',
      topProducts: 'Top catégories',
      hourlyTraffic: 'Trafic par heure',
      giftCardsSold: 'Cartes cadeaux vendues',
      giftCardsRedeemed: 'Cartes cadeaux utilisées',
      vsLastPeriod: 'vs période préc.',
      newVsReturning: 'Nouveaux vs Récurrents',
      new: 'Nouveaux',
      returning: 'Récurrents',
    },
    en: {
      title: 'Analytics & Statistics',
      period: 'Period',
      week: '7 days',
      month: '30 days',
      year: '12 months',
      totalRevenue: 'Total Revenue',
      transactions: 'Transactions',
      avgTicket: 'Avg. Ticket',
      newCustomers: 'New Customers',
      repeatRate: 'Repeat Rate',
      pointsIssued: 'Points Issued',
      revenueOverTime: 'Revenue Over Time',
      customerLoyalty: 'Customer Loyalty',
      topProducts: 'Top Categories',
      hourlyTraffic: 'Hourly Traffic',
      giftCardsSold: 'Gift Cards Sold',
      giftCardsRedeemed: 'Gift Cards Redeemed',
      vsLastPeriod: 'vs last period',
      newVsReturning: 'New vs Returning',
      new: 'New',
      returning: 'Returning',
    }
  }[language];

  useEffect(() => {
    generateMockData();
  }, [period]);

  // Generate realistic mock data
  const generateMockData = () => {
    setLoading(true);
    
    const days = period === 'week' ? 7 : period === 'month' ? 30 : 12;
    const isMonths = period === 'year';
    
    // Revenue over time
    const revenueData = [];
    const monthNames = language === 'fr' 
      ? ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dayNames = language === 'fr'
      ? ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date();
      if (isMonths) {
        date.setMonth(date.getMonth() - i);
      } else {
        date.setDate(date.getDate() - i);
      }
      
      const baseRevenue = isMonths ? 8000 : 400;
      const variance = Math.random() * (isMonths ? 4000 : 200);
      const dayOfWeek = date.getDay();
      const weekendBonus = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.3 : 1;
      
      revenueData.push({
        name: isMonths 
          ? monthNames[date.getMonth()]
          : period === 'week' 
            ? dayNames[date.getDay()]
            : `${date.getDate()}/${date.getMonth() + 1}`,
        revenue: Math.round((baseRevenue + variance) * weekendBonus),
        transactions: Math.round((15 + Math.random() * 10) * weekendBonus),
        giftCards: Math.round(Math.random() * 5),
      });
    }

    // Hourly traffic
    const hourlyData = [];
    for (let h = 8; h <= 22; h++) {
      const peakHour = h >= 12 && h <= 14 || h >= 18 && h <= 20;
      const baseTraffic = peakHour ? 25 : 10;
      hourlyData.push({
        hour: `${h}:00`,
        traffic: Math.round(baseTraffic + Math.random() * 15),
      });
    }

    // Customer loyalty pie
    const loyaltyData = [
      { name: t.new, value: 35, color: COLORS.accent1 },
      { name: t.returning, value: 65, color: COLORS.gold },
    ];

    // Top categories
    const categoryData = [
      { name: language === 'fr' ? 'Boissons' : 'Beverages', value: 42 },
      { name: language === 'fr' ? 'Pâtisseries' : 'Pastries', value: 28 },
      { name: language === 'fr' ? 'Repas' : 'Meals', value: 18 },
      { name: language === 'fr' ? 'Autres' : 'Other', value: 12 },
    ];

    // Summary metrics
    const totalRevenue = revenueData.reduce((sum, d) => sum + d.revenue, 0);
    const totalTransactions = revenueData.reduce((sum, d) => sum + d.transactions, 0);
    const totalGiftCards = revenueData.reduce((sum, d) => sum + d.giftCards, 0);

    setAnalyticsData({
      revenueData,
      hourlyData,
      loyaltyData,
      categoryData,
      summary: {
        totalRevenue,
        revenueChange: Math.round((Math.random() - 0.3) * 30),
        totalTransactions,
        transactionChange: Math.round((Math.random() - 0.3) * 20),
        avgTicket: Math.round(totalRevenue / totalTransactions * 100) / 100,
        ticketChange: Math.round((Math.random() - 0.4) * 15),
        newCustomers: Math.round(35 + Math.random() * 20),
        customerChange: Math.round((Math.random() - 0.2) * 25),
        repeatRate: Math.round(60 + Math.random() * 20),
        pointsIssued: Math.round(totalTransactions * 45),
        giftCardsSold: totalGiftCards,
        giftCardsRedeemed: Math.round(totalGiftCards * 0.7),
      }
    });

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#C3A365] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { summary, revenueData, hourlyData, loyaltyData, categoryData } = analyticsData;

  const handleExportCSV = () => {
    try {
      exportFullAnalytics(analyticsData, period, language);
      toast.success(language === 'fr' ? 'Export CSV téléchargé!' : 'CSV export downloaded!');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur d\'export' : 'Export failed');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Period Selector and Export Button */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h2 className="text-xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {t.title}
        </h2>
        <div className="flex items-center gap-3">
          {/* Export CSV Button */}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 bg-[#1E2F26] border border-[#283D32] hover:border-[#C3A365]/50 rounded-lg text-[#8BA89A] hover:text-white transition-colors"
            data-testid="export-csv-btn"
          >
            <DownloadSimple size={18} />
            <span className="text-sm font-medium">
              {language === 'fr' ? 'Exporter CSV' : 'Export CSV'}
            </span>
          </button>
          
          {/* Period Selector */}
          <div className="flex items-center gap-1 bg-[#1E2F26] border border-[#283D32] rounded-lg p-1">
            {[
              { key: 'week', label: t.week },
              { key: 'month', label: t.month },
              { key: 'year', label: t.year },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                className={`px-3 py-2 rounded-md text-sm transition-all ${
                  period === key
                    ? 'bg-[#C3A365] text-[#0F1A14]'
                    : 'text-[#8BA89A] hover:text-white'
                }`}
                data-testid={`period-${key}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <MetricCard
          icon={CreditCard}
          title={t.totalRevenue}
          value={summary.totalRevenue}
          change={summary.revenueChange}
          prefix="£"
        />
        <MetricCard
          icon={TrendUp}
          title={t.transactions}
          value={summary.totalTransactions}
          change={summary.transactionChange}
        />
        <MetricCard
          icon={Star}
          title={t.avgTicket}
          value={summary.avgTicket}
          change={summary.ticketChange}
          prefix="£"
        />
        <MetricCard
          icon={Users}
          title={t.newCustomers}
          value={summary.newCustomers}
          change={summary.customerChange}
        />
        <MetricCard
          icon={Gift}
          title={t.giftCardsSold}
          value={summary.giftCardsSold}
        />
        <MetricCard
          icon={TrendUp}
          title={t.repeatRate}
          value={summary.repeatRate}
          suffix="%"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue Over Time - Large */}
        <div className="lg:col-span-2 bg-[#1E2F26] border border-[#283D32] rounded-xl p-5">
          <h3 className="text-white text-sm font-medium mb-4">{t.revenueOverTime}</h3>
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={revenueData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.gold} stopOpacity={0.3}/>
                    <stop offset="95%" stopColor={COLORS.gold} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.green} opacity={0.3} />
                <XAxis 
                  dataKey="name" 
                  stroke={COLORS.lightGreen} 
                  fontSize={11}
                  tickLine={false}
                />
                <YAxis 
                  stroke={COLORS.lightGreen} 
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(value) => `£${value}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area 
                  type="monotone" 
                  dataKey="revenue" 
                  name="Revenue (£)"
                  stroke={COLORS.gold} 
                  fillOpacity={1} 
                  fill="url(#colorRevenue)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Loyalty Pie */}
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-5">
          <h3 className="text-white text-sm font-medium mb-4">{t.newVsReturning}</h3>
          <div className="h-[280px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={loyaltyData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {loyaltyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend 
                  verticalAlign="bottom"
                  formatter={legendFormatter}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Traffic */}
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-5">
          <h3 className="text-white text-sm font-medium mb-4">{t.hourlyTraffic}</h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.green} opacity={0.3} />
                <XAxis 
                  dataKey="hour" 
                  stroke={COLORS.lightGreen} 
                  fontSize={10}
                  tickLine={false}
                />
                <YAxis 
                  stroke={COLORS.lightGreen} 
                  fontSize={11}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="traffic" 
                  name="Visitors"
                  fill={COLORS.gold} 
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Categories */}
        <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-5">
          <h3 className="text-white text-sm font-medium mb-4">{t.topProducts}</h3>
          <div className="h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.green} opacity={0.3} />
                <XAxis 
                  type="number"
                  stroke={COLORS.lightGreen} 
                  fontSize={11}
                  tickLine={false}
                  tickFormatter={(value) => `${value}%`}
                />
                <YAxis 
                  type="category"
                  dataKey="name" 
                  stroke={COLORS.lightGreen} 
                  fontSize={11}
                  tickLine={false}
                  width={80}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="value" 
                  name="Share (%)"
                  fill={COLORS.gold} 
                  radius={[0, 4, 4, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Transactions Chart */}
      <div className="bg-[#1E2F26] border border-[#283D32] rounded-xl p-5">
        <h3 className="text-white text-sm font-medium mb-4">
          {language === 'fr' ? 'Transactions & Cartes Cadeaux' : 'Transactions & Gift Cards'}
        </h3>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={revenueData}>
              <CartesianGrid strokeDasharray="3 3" stroke={COLORS.green} opacity={0.3} />
              <XAxis 
                dataKey="name" 
                stroke={COLORS.lightGreen} 
                fontSize={11}
                tickLine={false}
              />
              <YAxis 
                stroke={COLORS.lightGreen} 
                fontSize={11}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top"
                height={36}
                formatter={legendFormatter}
              />
              <Line 
                type="monotone" 
                dataKey="transactions" 
                name="Transactions"
                stroke={COLORS.gold} 
                strokeWidth={2}
                dot={{ fill: COLORS.gold, strokeWidth: 0, r: 3 }}
              />
              <Line 
                type="monotone" 
                dataKey="giftCards" 
                name="Gift Cards"
                stroke={COLORS.accent1} 
                strokeWidth={2}
                dot={{ fill: COLORS.accent1, strokeWidth: 0, r: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default MerchantAnalytics;
